import React, { useEffect, useMemo, useRef, useState } from 'react';
import api, { formatApiError } from '../api/client';
import Modal from '../components/Modal';
import { StatusBadge } from './Dashboard';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Search, LogOut, Send, Camera, Upload, X, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { isValidPhone } from '../lib/validate';

const TYPES = [
  { value: 'business', label: 'Business', color: 'bg-blue-100 text-blue-700' },
  { value: 'vendor', label: 'Vendor', color: 'bg-purple-100 text-purple-700' },
  { value: 'candidate', label: 'Candidate', color: 'bg-orange-100 text-primary' },
  { value: 'regular', label: 'Regular', color: 'bg-zinc-200 text-zinc-700' },
  { value: 'other', label: 'Other', color: 'bg-yellow-100 text-yellow-700' },
];

// Same options as the mobile form.
const QUALIFICATIONS = ['10th', '12th', 'SSC', 'Graduate', 'P.G.', 'Diploma', 'Other'];

// Visitors registered through the request flow have no check-in status until their
// gate pass is scanned, so show where the request stands instead.
const REQUEST_BADGE = {
  pending:  { cls: 'badge-warn',   label: 'Pending approval' },
  approved: { cls: 'badge-info',   label: 'Approved · not arrived' },
  rejected: { cls: 'badge-danger', label: 'Rejected' },
};

function VisitorBadge({ v }) {
  if (v.status) return <StatusBadge status={v.status} />;
  const r = REQUEST_BADGE[v.request_status];
  return r ? <span className={r.cls}>{r.label}</span> : null;
}

export default function Visitors() {
  const { user } = useAuth();
  // Only guard and admin can register visitors (same as mobile; the API allows only these).
  const canRegister = ['guard', 'admin'].includes(user?.role);
  const [visitors, setVisitors] = useState([]);
  const [status, setStatus] = useState('all');
  const [vType, setVType] = useState('all');
  const [q, setQ] = useState('');
  const [openCreate, setOpenCreate] = useState(false);

  const load = async () => {
    const params = {};
    if (status !== 'all') params.status = status;
    if (vType !== 'all') params.visitor_type = vType;
    if (q) params.q = q;
    const { data } = await api.get('/visitors', { params });
    setVisitors(data);
  };

  useEffect(() => { load(); }, [status, vType]);

  const onCheckout = async (v) => {
    try {
      await api.post(`/visitors/${v.id}/checkout`);
      toast.success(`${v.name} checked out`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, e?.response?.data));
    }
  };

  return (
    <div className="space-y-6" data-testid="visitors-page">
      <header className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Module 01</div>
          <h1 className="heading text-3xl font-black text-ink">Visitors</h1>
          <p className="text-sm text-zinc-500 mt-1">Business, vendor, candidate & regular entries.</p>
        </div>
        {canRegister && (
          <button className="btn-primary" onClick={() => setOpenCreate(true)} data-testid="new-visitor-btn">
            <Plus size={16} /> New Visitor Entry
          </button>
        )}
      </header>

      <div className="card p-3 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input className="input pl-9" placeholder="Search name or phone…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} data-testid="search-visitors" />
        </div>
        <select className="input max-w-[160px]" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="filter-status">
          <option value="all">All statuses</option>
          <option value="checked_in">Checked In</option>
          <option value="checked_out">Checked Out</option>
        </select>
        <select className="input max-w-[160px]" value={vType} onChange={(e) => setVType(e.target.value)} data-testid="filter-type">
          <option value="all">All types</option>
          {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {visitors.length === 0 && <div className="card p-12 text-center text-zinc-400 col-span-full">No visitors found.</div>}
        {visitors.map((v) => {
          const typeColor = TYPES.find((t) => t.value === v.visitor_type)?.color || 'bg-zinc-100 text-zinc-700';
          return (
            <div key={v.id} className="card p-4 card-hover" data-testid={`visitor-${v.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 bg-zinc-900 text-white rounded-sm flex items-center justify-center font-bold">
                    {(v.name || '?').slice(0,1).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-bold text-ink">{v.name}</div>
                    <div className="text-xs text-zinc-500 font-mono">{v.phone}</div>
                  </div>
                </div>
                <VisitorBadge v={v} />
              </div>
              <div className="flex gap-2 flex-wrap text-[10px] mb-3">
                <span className={`badge ${typeColor}`}>{v.visitor_type}</span>
                {v.vehicle_number && <span className="badge-muted">🚗 {v.vehicle_number}</span>}
              </div>
              {v.purpose && <div className="text-sm text-zinc-600 mb-2"><span className="overline mr-2">Purpose</span>{v.purpose}</div>}
              {v.to_meet && <div className="text-xs text-zinc-500">To meet: <span className="font-semibold text-ink">{v.to_meet}</span></div>}
              {v.department && <div className="text-xs text-zinc-500">Dept: {v.department}</div>}
              <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center justify-between text-xs">
                <div className="font-mono font-bold text-ink">{v.pass_id}</div>
                {v.status === 'checked_in' ? (
                  <button className="btn-outline text-xs" onClick={() => onCheckout(v)} data-testid={`checkout-${v.id}`}>
                    <LogOut size={12} /> Check Out
                  </button>
                ) : v.check_out ? (
                  <div className="text-zinc-400">Out: {format(new Date(v.check_out), 'dd MMM HH:mm')}</div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {openCreate && <CreateVisitorModal onClose={() => setOpenCreate(false)} onCreated={() => { setOpenCreate(false); load(); }} />}
    </div>
  );
}

// ── Register Visitor (web port of mobile's CreateSheet) ─────────────────────────
// Submits a visitor REQUEST: pending until the assigned person approves it, after
// which a QR gate pass is issued and scanned at the gate.

const EMPTY_CANDIDATE = {
  applied_for: '', dob: '', fathers_name: '', qualification: '',
  previous_employer: '', experience_years: '', current_employer: '',
  current_salary: '', expected_salary: '', reason_for_leaving: '', referred_by: '',
};

// Square centre-crop + JPEG compression, comparable to mobile's 1:1 crop at quality 0.5.
const PHOTO_SIZE = 600;
function toPhotoDataUrl(source, srcW, srcH) {
  const side = Math.min(srcW, srcH);
  const c = document.createElement('canvas');
  c.width = PHOTO_SIZE; c.height = PHOTO_SIZE;
  c.getContext('2d').drawImage(source, (srcW - side) / 2, (srcH - side) / 2, side, side, 0, 0, PHOTO_SIZE, PHOTO_SIZE);
  return c.toDataURL('image/jpeg', 0.6);
}

function fileToPhotoDataUrl(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { resolve(toPhotoDataUrl(img, img.naturalWidth, img.naturalHeight)); URL.revokeObjectURL(url); };
    img.onerror = () => { reject(new Error('Not an image')); URL.revokeObjectURL(url); };
    img.src = url;
  });
}

// Phones/tablets open the camera straight from a file input; desktops use a live webcam,
// which browsers only allow on HTTPS or localhost.
const isTouchDevice = () => window.matchMedia?.('(pointer: coarse)').matches;
const canUseWebcam = () => window.isSecureContext && !!navigator.mediaDevices?.getUserMedia;

function Field({ label, error, children }) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
      {error && <div className="text-xs text-red-700 mt-1">{error}</div>}
    </div>
  );
}

function PhotoPicker({ value, onChange }) {
  const uploadRef = useRef(null);
  const cameraRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [webcamOn, setWebcamOn] = useState(false);
  const touch = isTouchDevice();
  const webcam = !touch && canUseWebcam();

  const stopWebcam = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setWebcamOn(false);
  };
  useEffect(() => stopWebcam, []);
  // Attach the stream once the <video> element exists.
  useEffect(() => {
    if (webcamOn && videoRef.current && streamRef.current) videoRef.current.srcObject = streamRef.current;
  }, [webcamOn]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try { onChange(await fileToPhotoDataUrl(file)); }
    catch { toast.error('Could not read that image.'); }
  };

  const startWebcam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
      streamRef.current = stream;
      setWebcamOn(true);
    } catch {
      toast.error('Camera not accessible. Use Upload instead.');
    }
  };

  const snap = () => {
    const v = videoRef.current;
    if (!v?.videoWidth) return;
    onChange(toPhotoDataUrl(v, v.videoWidth, v.videoHeight));
    stopWebcam();
  };

  const takePhoto = () => (touch ? cameraRef.current?.click() : startWebcam());

  return (
    <div className="flex gap-3 items-start">
      {webcamOn ? (
        <div className="space-y-2">
          <video ref={videoRef} autoPlay playsInline muted className="w-48 h-48 object-cover bg-zinc-900 rounded-sm" />
          <div className="flex gap-2">
            <button type="button" className="btn-primary text-xs" onClick={snap} data-testid="snap-photo"><Camera size={12} /> Capture</button>
            <button type="button" className="btn-outline text-xs" onClick={stopWebcam}>Cancel</button>
          </div>
        </div>
      ) : (
        <>
          {value ? (
            <div className="relative">
              <img src={value} alt="Visitor" className="w-[72px] h-[72px] object-cover rounded-sm" />
              <button type="button" onClick={() => onChange('')} className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-zinc-900 text-white flex items-center justify-center" aria-label="Remove photo">
                <X size={12} />
              </button>
            </div>
          ) : (
            <div className="w-[72px] h-[72px] border-2 border-dashed border-zinc-300 rounded-sm flex items-center justify-center text-zinc-400">
              <Camera size={24} />
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            {(touch || webcam) && (
              <button type="button" className="btn-outline text-xs" onClick={takePhoto} data-testid="capture-photo"><Camera size={12} /> Take Photo</button>
            )}
            <button type="button" className="btn-outline text-xs" onClick={() => uploadRef.current?.click()} data-testid="pick-photo"><Upload size={12} /> Upload</button>
          </div>
        </>
      )}
      <input ref={uploadRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} />
    </div>
  );
}

function EmployeePicker({ value, onChange, error }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get('/users')
      // Active users only — a deactivated account could never log in to approve.
      .then(({ data }) => setEmployees(data.filter((u) => u.active && u.role !== 'guard')))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return employees;
    return employees.filter((e) =>
      e.name?.toLowerCase().includes(s) ||
      e.department?.toLowerCase().includes(s) ||
      e.role?.toLowerCase().includes(s));
  }, [employees, search]);

  return (
    <div>
      <button
        type="button"
        className={`input text-left flex items-center justify-between ${error ? 'border-red-400' : ''}`}
        onClick={() => { setOpen((o) => !o); setSearch(''); }}
        data-testid="v-employee"
      >
        <span className={value ? 'text-ink' : 'text-zinc-400'}>
          {value ? `${value.name}${value.department ? ' · ' + value.department : ''}` : 'Select employee'}
        </span>
        <ChevronDown size={14} className="text-zinc-400" />
      </button>
      {open && (
        <div className="border border-zinc-300 rounded-sm mt-1 bg-white">
          <input
            className="w-full px-3 py-2 text-sm border-b border-zinc-200 outline-none"
            placeholder="Search by name or department..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <div className="max-h-56 overflow-y-auto">
            {loading && <div className="p-4 text-center text-sm text-zinc-400">Loading…</div>}
            {!loading && filtered.map((emp) => (
              <button
                type="button"
                key={emp.id}
                onClick={() => { onChange(emp); setOpen(false); setSearch(''); }}
                className={`w-full text-left px-3 py-2 border-b border-zinc-100 hover:bg-zinc-50 ${value?.id === emp.id ? 'bg-blue-50' : ''}`}
              >
                <div className="font-bold text-sm text-ink">{emp.name}</div>
                <div className="text-xs text-zinc-500">{[emp.role, emp.department].filter(Boolean).join(' · ')}</div>
              </button>
            ))}
            {!loading && filtered.length === 0 && <div className="p-4 text-center text-sm text-zinc-400">No employees found</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// Mobile stores date of birth as DD/MM/YYYY; <input type="date"> uses YYYY-MM-DD.
const dobToInput = (dob) => {
  if (!dob) return '';
  const [d, m, y] = dob.split('/');
  return y && m && d ? `${y}-${m}-${d}` : '';
};
const inputToDob = (val) => {
  if (!val) return '';
  const [y, m, d] = val.split('-');
  return `${d}/${m}/${y}`;
};

function CandidateSection({ data, onChange, errors }) {
  const set = (key, val) => onChange((prev) => ({ ...prev, [key]: val }));
  const today = format(new Date(), 'yyyy-MM-dd');
  return (
    <div className="space-y-4 border-t border-zinc-200 pt-4">
      <div className="heading font-black text-ink">Employment Details</div>
      <Field label="Applied for Post *" error={errors.applied_for}>
        <input className={`input ${errors.applied_for ? 'border-red-400' : ''}`} value={data.applied_for} maxLength={100}
          placeholder="e.g. Sales Executive" onChange={(e) => set('applied_for', e.target.value)} data-testid="c-applied-for" />
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Date of Birth">
          <input type="date" className="input" max={today} value={dobToInput(data.dob)} onChange={(e) => set('dob', inputToDob(e.target.value))} />
        </Field>
        <Field label="Father's / Husband's Name">
          <input className="input" value={data.fathers_name} maxLength={100} onChange={(e) => set('fathers_name', e.target.value)} />
        </Field>
      </div>
      <Field label="Qualification">
        <div className="flex flex-wrap gap-1.5">
          {QUALIFICATIONS.map((qual) => (
            <button key={qual} type="button"
              onClick={() => set('qualification', data.qualification === qual ? '' : qual)}
              className={`px-3 py-1.5 text-xs font-bold border rounded-sm ${data.qualification === qual ? 'bg-primary text-white border-primary' : 'bg-white border-zinc-300 hover:border-zinc-500'}`}
            >{qual}</button>
          ))}
        </div>
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Previous Employer">
          <input className="input" value={data.previous_employer} maxLength={100} onChange={(e) => set('previous_employer', e.target.value)} />
        </Field>
        <Field label="Experience (Years)">
          <input className="input" inputMode="decimal" value={data.experience_years} maxLength={5} placeholder="e.g. 3.5"
            onChange={(e) => set('experience_years', e.target.value.replace(/[^0-9.]/g, ''))} />
        </Field>
      </div>
      <Field label="Current Employer">
        <input className="input" value={data.current_employer} maxLength={100} onChange={(e) => set('current_employer', e.target.value)} />
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Current Salary (₹)">
          <input className="input" inputMode="numeric" value={data.current_salary} maxLength={10} placeholder="0"
            onChange={(e) => set('current_salary', e.target.value.replace(/[^0-9]/g, ''))} />
        </Field>
        <Field label="Expected Salary (₹)">
          <input className="input" inputMode="numeric" value={data.expected_salary} maxLength={10} placeholder="0"
            onChange={(e) => set('expected_salary', e.target.value.replace(/[^0-9]/g, ''))} />
        </Field>
      </div>
      <Field label="Reason for Leaving Current Job">
        <textarea rows={2} className="input" value={data.reason_for_leaving} maxLength={500} onChange={(e) => set('reason_for_leaving', e.target.value)} />
      </Field>
      <Field label="Referred By">
        <input className="input" value={data.referred_by} maxLength={100} placeholder="Name of referrer (if any)" onChange={(e) => set('referred_by', e.target.value)} />
      </Field>
    </div>
  );
}

function CreateVisitorModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '', phone: '', visitor_type: 'business', purpose: '', vehicle_number: '', photo_url: '',
  });
  const [employee, setEmployee] = useState(null);
  const [candidate, setCandidate] = useState(EMPTY_CANDIDATE);
  const [errors, setErrors] = useState({});
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const setField = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setErrors((e) => ({ ...e, [key]: '' }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const phone = form.phone.trim();
    const errs = {};
    if (!name) errs.name = 'Full name is required.';
    if (!phone) errs.phone = 'Phone number is required.';
    else if (!isValidPhone(phone)) errs.phone = 'Enter a valid 10-digit Indian mobile number.';
    if (!employee) errs.employee = 'Please select the employee being visited.';
    if (form.visitor_type === 'candidate' && !candidate.applied_for.trim()) errs.applied_for = 'Post applied for is required.';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({}); setErr('');
    setLoading(true);
    try {
      const payload = { ...form, name, phone, assigned_employee_id: employee.id };
      if (form.visitor_type === 'candidate') {
        payload.candidate_info = {
          ...candidate,
          applied_for: candidate.applied_for.trim(),
          experience_years: candidate.experience_years ? Number(candidate.experience_years) : null,
          current_salary: candidate.current_salary ? Number(candidate.current_salary) : null,
          expected_salary: candidate.expected_salary ? Number(candidate.expected_salary) : null,
        };
      }
      await api.post('/visitor-requests', payload);
      toast.success(`Request sent to ${employee.name} for approval.`);
      onCreated();
    } catch (e2) {
      setErr(formatApiError(e2?.response?.data?.detail, e2?.response?.data));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Register Visitor"
      subtitle="New Request"
      size="md"
      footer={
        <>
          <button className="btn-outline" onClick={onClose}>Cancel</button>
          <button form="v-form" type="submit" className="btn-primary" disabled={loading} data-testid="submit-visitor">
            <Send size={14} /> {loading ? 'Sending…' : 'Send Request'}
          </button>
        </>
      }
    >
      <form id="v-form" onSubmit={submit} className="space-y-4" noValidate>
        <PhotoPicker value={form.photo_url} onChange={(url) => setField('photo_url', url)} />

        <div className="grid grid-cols-2 gap-4">
          <Field label="Full Name *" error={errors.name}>
            <input className={`input ${errors.name ? 'border-red-400' : ''}`} value={form.name} maxLength={100}
              onChange={(e) => setField('name', e.target.value)} data-testid="v-name" />
          </Field>
          <Field label="Phone *" error={errors.phone}>
            <input className={`input ${errors.phone ? 'border-red-400' : ''}`} value={form.phone} maxLength={20} inputMode="tel"
              onChange={(e) => setField('phone', e.target.value)} data-testid="v-phone" />
          </Field>
        </div>

        <Field label="Visitor Type *">
          <div className="grid grid-cols-5 gap-1">
            {TYPES.map((t) => (
              <button key={t.value} type="button" onClick={() => setField('visitor_type', t.value)}
                data-testid={`v-type-${t.value}`}
                className={`px-2 py-2 text-xs font-bold uppercase border rounded-sm ${
                  form.visitor_type === t.value ? 'bg-ink text-white border-ink' : 'bg-white border-zinc-300 hover:border-zinc-500'
                }`}
              >{t.label}</button>
            ))}
          </div>
        </Field>

        <Field label="Visiting Whom *" error={errors.employee}>
          <EmployeePicker
            value={employee}
            onChange={(emp) => { setEmployee(emp); setErrors((e) => ({ ...e, employee: '' })); }}
            error={errors.employee}
          />
        </Field>

        <Field label="Vehicle Number">
          <input className="input uppercase" value={form.vehicle_number} maxLength={20}
            onChange={(e) => setField('vehicle_number', e.target.value.replace(/[^A-Z0-9\s-]/gi, '').toUpperCase())} />
        </Field>

        <Field label="Purpose">
          <textarea rows={2} className="input" value={form.purpose} maxLength={500}
            onChange={(e) => setField('purpose', e.target.value)} data-testid="v-purpose" />
        </Field>

        {form.visitor_type === 'candidate' && (
          <CandidateSection data={candidate} onChange={setCandidate} errors={errors} />
        )}

        {err && <div className="text-red-700 text-sm bg-red-50 border border-red-200 p-2 rounded-sm">{err}</div>}
      </form>
    </Modal>
  );
}
